// Where a chat line is filed, decided when the work that writes it STARTS.
//
// The selection (`activeRunId`) answers one question: what to render. It must never answer
// "which forecast does this line belong to?" for work that finishes later, because the user may
// have selected another forecast by then. So deferred work captures a target here at initiation
// (a chat turn at send, a bug report at submit, a forecast's own summary from its run id) and
// resolves it against the live store BY THAT ID when the line is written: the data is fresh, the
// identity is fixed.
import { DRAFT_CONV_ID } from "./draft.js";
import type { Run } from "./model.js";
import type { RunState } from "./reducer.js";
import { selectActiveRun, selectRun } from "./selectors.js";

/** Pure module: no React, DOM or network dependencies. */

/**
 * A chat line's destination, fixed at initiation: a run, by its `runId` or else its
 * `forecastId`. Naming neither is the draft conversation (no forecast yet). `forecastId` is
 * what the run had at capture; the live run's wins at resolution, since a pending run gains its
 * row after the work starts.
 */
export interface ChatTarget {
  readonly runId: string | null;
  readonly forecastId?: string | null;
  /**
   * The user message this line answers. A draft question can be adopted into a forecast's
   * conversation while its answer is pending (submit adopts the draft); the answer follows it,
   * and is dropped once that forecast is gone.
   */
  readonly msgId?: string | null;
}

/**
 * The one sanctioned way to file a chat line under whatever is selected WHEN IT IS WRITTEN: for
 * synchronous view code (a click handler) and for tour narration, which describes the screen the
 * user is looking at. Deferred work passes a captured {@link ChatTarget}. Kept as a named
 * sentinel, never a default, so every use is greppable and counted by the ratchet
 * (`untargetedSayerMsgs`).
 */
export const LIVE_SELECTION: unique symbol = Symbol("LIVE_SELECTION");

/** The draft conversation: the chat before any forecast exists. */
export const DRAFT_TARGET: ChatTarget = Object.freeze({ runId: null, forecastId: null });

/** A specific run's thread, for work that belongs to that run (its summary, its error line). */
export function chatTargetForRun(
  runId: string | null,
  forecastId: string | null = null,
): ChatTarget {
  return { runId, forecastId };
}

/**
 * Capture the selection as a target. Call it ONCE, where the work starts, before any await;
 * never again in a continuation. With nothing selected (or a selection naming no loaded run,
 * which the chat panel also renders as the draft) the target is the draft conversation.
 */
export function captureChatTarget(
  state: RunState,
  { msgId = null }: { msgId?: string | null } = {},
): ChatTarget {
  const run = selectActiveRun(state);
  const base = run ? chatTargetForRun(run.runId, run.forecastId ?? null) : DRAFT_TARGET;
  return msgId ? { ...base, msgId } : base;
}

/** A captured target, resolved against the live store. */
export interface ResolvedChatTarget {
  convId: string;
  /** The row the line persists against; null when there is none (the draft, or no row yet). */
  forecastId: string | null;
  /** The run the line belongs to; null for the draft. */
  run: Run | null;
  draft: boolean;
}

const DRAFT: ResolvedChatTarget = Object.freeze({
  convId: DRAFT_CONV_ID,
  forecastId: null,
  run: null,
  draft: true,
});

function findByForecastId(map: Record<string, Run> | undefined, forecastId: string): Run | null {
  // `for...in`, not `Object.values(...).find`: no throwaway array of every run per lookup.
  for (const k in map ?? {}) {
    if (map![k]?.forecastId === forecastId) return map![k]!;
  }
  return null;
}

function runTarget(state: RunState, run: Run, captured: ChatTarget): ResolvedChatTarget {
  const convId = run.conversationId || run.runId;
  // Persist against the row the conversation reloads from: a branch's thread is its root's.
  const owner = selectRun(state, convId) ?? run;
  return {
    convId,
    forecastId: owner.forecastId ?? run.forecastId ?? captured.forecastId ?? null,
    run,
    draft: false,
  };
}

/**
 * Resolve `target` against the live store. Returns null when its run is gone (removed, cleared,
 * access lost): the caller must DROP the line, never re-target it at the selection. A draft
 * target whose question was adopted into a forecast follows the question there, and is dropped
 * with that forecast.
 */
export function resolveChatTarget(state: RunState, target: ChatTarget): ResolvedChatTarget | null {
  if (target.runId == null && !target.forecastId) {
    // Not adopted: still in the draft, or not filed at all (a refusal of a question submit
    // took straight from the input).
    const adopterId = target.msgId ? state.adoptedDraftMsgs[target.msgId] : undefined;
    if (adopterId === undefined) return DRAFT;
    const adopter = selectRun(state, adopterId);
    return adopter ? runTarget(state, adopter, { runId: adopter.runId }) : null;
  }
  const run =
    (target.runId != null ? selectRun(state, target.runId) : null) ??
    (target.forecastId
      ? (findByForecastId(state.runsById, target.forecastId) ??
        findByForecastId(state.sharedRunsById, target.forecastId))
      : null);
  return run ? runTarget(state, run, target) : null;
}
